import { ApolloError } from '@apollo/client';
import { UUID } from '@cmg/common';
import { Divider, MenuList } from '@cmg/design-system';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import React from 'react';

import { SNotFound } from '../../../../../../common/components/errorRenderer/SNotFound/SNotFound';
import ServerErrorsBanner from '../../../../../../common/components/indicators/server-error/ServerErrorsBanner';
import { useDocumentTitle } from '../../../../../../common/hooks/useDocumentTitle/useDocumentTitle';
import routeFactory from '../../../../../../common/util/routeFactory';
import { OfferingType } from '../../../../../../graphql';
import { parsePartialErrors } from '../../../../../../graphql/parsePartialErrors';
import { useSidePanelSellSideNavItems } from '../../../../hooks/useSidePanelSellSideNavItems';
import { useSideBar_OfferingDetails_OfferingQuery } from '../../graphql';
import ConvertibleOfferingStatusIndicator from '../offering-status-indicator/ConvertibleOfferingStatusIndicator';
import OfferingStatusIndicator from '../offering-status-indicator/OfferingStatusIndicator';
import { SideNavbarItem } from './SideNavbarItem';
import { SideNavbarSubNavigationItem } from './SideNavbarSubNavigationItem';
import { Layout } from './SidePanelNavbar.styles';

const EMPTY_ARRAY = [];
export type Props = {
  readonly offeringId: UUID;
  baseSidePanelUrl: string;
};

export const SidePanelSellSideNavbar: React.FC<Props> = ({ offeringId, baseSidePanelUrl }) => {
  const { data, error, loading } = useSideBar_OfferingDetails_OfferingQuery({
    variables: { offeringId },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });
  const [publishedOffering] = data?.publishedOfferings?.items ?? EMPTY_ARRAY;

  useDocumentTitle(
    routeFactory.offeringSidePanel.getDocumentTitle({
      ticker: publishedOffering?.pricingInstrumentStockSymbol,
    })
  );

  const navItems = useSidePanelSellSideNavItems({ offeringId, baseSidePanelUrl });

  if (isNil(publishedOffering) && !loading && !error) {
    return <SNotFound />;
  }

  const classifiedErrors = parsePartialErrors(error);

  if (!isEmpty(classifiedErrors?.errors)) {
    return (
      <ServerErrorsBanner error={new ApolloError({ graphQLErrors: classifiedErrors?.errors })} />
    );
  }

  return (
    <Layout error={false} data-testid="side-panel-sell-side-navbar">
      <React.Fragment>
        {data && (
          <React.Fragment>
            {publishedOffering.type === OfferingType.Convertible ? (
              <ConvertibleOfferingStatusIndicator
                publishedOffering={publishedOffering}
                loading={loading}
              />
            ) : (
              <OfferingStatusIndicator publishedOffering={publishedOffering} isBusy={loading} />
            )}
            <Divider />
          </React.Fragment>
        )}
        {navItems && (
          <MenuList
            disablePadding={false}
            disableListWrap={false}
            component="nav"
            aria-labelledby="sell-side-nav"
          >
            {navItems.map(navItem => {
              if ('items' in navItem) {
                return <SideNavbarSubNavigationItem key={navItem.id} navItemGroup={navItem} />;
              }
              return (
                <SideNavbarItem
                  key={navItem.id}
                  icon={navItem.icon}
                  href={navItem.href}
                  selected={navItem.selected}
                >
                  {navItem.label}
                </SideNavbarItem>
              );
            })}
          </MenuList>
        )}
      </React.Fragment>
    </Layout>
  );
};
